'use client';

import { IconButton } from '@magicschool/spellbook/components/IconButton';
import { type DynamicTextProp, useDynamicTextResolver } from '@magicschool/spellbook/hooks/useDynamicTextResolver';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { usePrefersReducedMotion } from '@magicschool/spellbook/hooks/usePrefersReducedMotion';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import { omit } from 'lodash-es';
import { useDirection } from 'radix-ui/direction';
import { type ComponentProps, cloneElement, type KeyboardEvent, type ReactElement, useCallback, useEffect, useState } from 'react';
import { CarouselItemSlot, type CarouselItemSlotProps } from './slots';

export type CarouselApi = UseEmblaCarouselType[1];
export type CarouselOptions = Parameters<typeof useEmblaCarousel>[0];

export type CarouselProps = {
  /** Override scroll next value. Otherwise scrolling is determine by current pane and pane count. */
  canScrollNext?: boolean;
  /** Override scroll previous value. Otherwise scrolling is determine by current pane and pane count. */
  canScrollPrev?: boolean;
  /** Optional Embla Carousel config options. */
  carouselOptions?: Omit<CarouselOptions, 'autoloop'>;
  /** ARIA labels applied on the next/previous buttons. */
  text: {
    nextLabel: string;
    previousLabel: string;
    liveArea: DynamicTextProp<[currentIndex: number, totalSlides: number]>;
  };
  /** ID attribute value applied to the carousel. */
  id?: string;
  /** Callback triggered when user settles on a panel. */
  onSlideChange?: (currentIndex: number, totalSlides: number, direction: 'next' | 'previous') => void;
  /** Used to override the Embla API with an externally managed instance. */
  setApi?: (api: CarouselApi) => void;
};

function CarouselBase({
  canScrollPrev: canScrollPrevProp,
  canScrollNext: canScrollNextProp,
  carouselOptions,
  className,
  children,
  text,
  id,
  onSlideChange,
  setApi,
  ...props
}: ComponentProps<'div'> & CarouselProps) {
  const direction = useDirection();
  const isRtl = direction === 'rtl';
  const prefersReducedMotion = usePrefersReducedMotion();

  const internalCarouselId = useId();
  const [carouselRef, api] = useEmblaCarousel({
    ...omit(carouselOptions, 'autoloop'),
    direction,
    axis: 'x',
  });

  const [canScrollPrevInternal, setCanScrollPrevInternal] = useState<boolean>(false);
  const [canScrollNextInternal, setCanScrollNextInternal] = useState<boolean>(false);

  const canScrollPrev = canScrollPrevProp ?? canScrollPrevInternal;
  const canScrollNext = canScrollNextProp ?? canScrollNextInternal;

  const handleSlideChange = useCallback(() => {
    if (!api || !onSlideChange) return;
    onSlideChange(
      api.selectedScrollSnap(),
      api.scrollSnapList().length,
      api.selectedScrollSnap() > api.previousScrollSnap() ? 'next' : 'previous',
    );
  }, [api, onSlideChange]);

  const updateControlState = useCallback((api: CarouselApi) => {
    if (!api) return;
    setCanScrollPrevInternal(api.canScrollPrev());
    setCanScrollNextInternal(api.canScrollNext());
  }, []);

  const onSelect = useCallback(
    (api: CarouselApi) => {
      if (!api) return;
      handleSlideChange();
      updateControlState(api);
    },
    [updateControlState, handleSlideChange],
  );

  const scrollPrev = useCallback(() => {
    if (api && canScrollPrev) {
      api.scrollPrev(prefersReducedMotion);
    }
  }, [api, canScrollPrev, prefersReducedMotion]);

  const scrollNext = useCallback(() => {
    if (api && canScrollNext) {
      api.scrollNext(prefersReducedMotion);
    }
  }, [api, canScrollNext, prefersReducedMotion]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  useEffect(() => {
    if (!api || !setApi) return;
    setApi(api);
  }, [api, setApi]);

  useEffect(() => {
    if (!api) return;
    updateControlState(api);

    api.on('reInit', updateControlState);
    api.on('select', onSelect);

    return () => {
      api?.off('select', onSelect);
    };
  }, [api, onSelect]);

  const { slots } = useSlots(children, {
    items: { Component: CarouselItemSlot, props: { api } },
  });

  const resolveLiveArea = useDynamicTextResolver(
    text.liveArea,
    (currentIndex, totalSlides) => `Slide ${currentIndex + 1} of ${totalSlides}`,
  );

  const carouselId = id ?? internalCarouselId;

  return (
    <div
      data-spellbook-name="carousel"
      id={carouselId}
      onKeyDownCapture={handleKeyDown}
      className={cn('grid relative w-full gap-2', className)}
      role="region"
      aria-roledescription="carousel"
      {...props}
    >
      <div ref={carouselRef} className="overflow-hidden">
        <div className="flex -ms-4">
          {(slots.items as ReactElement[]).map((item, index) =>
            cloneElement(item, { ...(item.props as object), index, key: item.key ?? index } as CarouselItemSlotProps),
          )}
        </div>
      </div>
      <div className="flex gap-4 justify-center">
        <IconButton
          variant="outlined"
          name={isRtl ? 'arrow-right' : 'arrow-left'}
          disabled={!canScrollPrev}
          onClick={scrollPrev}
          aria-label={text.previousLabel}
          aria-controls={carouselId}
        />
        <IconButton
          variant="outlined"
          name={isRtl ? 'arrow-left' : 'arrow-right'}
          disabled={!canScrollNext}
          onClick={scrollNext}
          aria-label={text.nextLabel}
          aria-controls={carouselId}
        />
      </div>
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {resolveLiveArea((api?.selectedScrollSnap() ?? 0) + 1, api?.scrollSnapList()?.length ?? 0)}
      </div>
    </div>
  );
}

/**
 * A carousel component that displays multiple panes of content that can be clicked or swiped through.
 *
 * Additional configuration options available through the `carouselOptions` prop, such as looping through
 * panels. See Embla Carousel's [docs][opts-docs] for details.
 *
 * [opts-docs]: https://www.embla-carousel.com/api/options/
 *
 * ### When to use
 * - A small set (five or fewer) of related panes the user steps through one at a time (testimonials, onboarding tips)
 *
 * ### When NOT to use
 * - Content users need to compare or scan at once: lay the panes out in a grid instead
 * - Paged data or long lists: use `Pagination` with a list, or `DataGrid`
 *
 * ### Available slots
 * - `<Carousel.Item>`: Individual carousel item/slide.
 */
export const Carousel = Object.assign(CarouselBase, {
  Item: CarouselItemSlot,
});
